import { useMemo } from 'react';
import type { IDraw } from '@apply/api/src/api/DrawApi';
import { useDrawBudgetQuery } from '../../queries/drawQueries';
import type { MobileDrawItem } from '../../draws_v2/DrawSummary/components/MobileDrawControl/types/MobileDrawItem';

export const useDeriveDrawItemsFromDrawBudgetId = (
    drawBudgetId: number
): { items: MobileDrawItem[]; isLoading: boolean } => {
    const { data: budget, isLoading } = useDrawBudgetQuery(drawBudgetId);

    const items = useMemo<MobileDrawItem[]>(() => {
        if (!budget) return [];

        const latestDraw = budget.draws[budget.draws.length - 1] as IDraw;
        let newItemIndex = -1;

        return budget.drawBudgetItems.map((drawBudgetItem) => {
            const history: number[] = [];
            for (let i = 0; i < budget.drawCount - 1; i++) {
                const drawItem = drawBudgetItem.drawItems?.find((x) => x.drawNumber === i);
                history.push(drawItem ? drawItem.funded : 0);
            }

            const fundedLineTotal = drawBudgetItem.drawItems?.reduce((sum, value) => sum + (value.funded ?? 0), 0) ?? 0;
            const amountDrawn = drawBudgetItem.acquisitionAmount + fundedLineTotal;
            const available = Math.round((drawBudgetItem.budgetAmount - drawBudgetItem.equityAmount - amountDrawn) * 100) / 100;

            const base: MobileDrawItem = {
                id: newItemIndex--,
                drawBudgetItemId: drawBudgetItem.id,
                borrowerRequest: 0,
                drawId: latestDraw.id,
                drawNumber: latestDraw.drawNumber,
                funded: 0,
                inspection: 0,
                notes: '',
                dirty: false,
                categoryId: drawBudgetItem.category,
                description: drawBudgetItem.description,
                budgetAmount: drawBudgetItem.budgetAmount,
                equityAmount: drawBudgetItem.equityAmount,
                acquisitionAmount: drawBudgetItem.acquisitionAmount,
                availableAmount: available,
                drawHistory: history,
            };

            const existing = drawBudgetItem.drawItems?.find((drawItem) => drawItem.drawNumber === budget.drawCount - 1);
            return existing ? { ...base, ...existing } : base;
        });
    }, [budget]);

    return { items, isLoading };
};
